import React, {useState} from 'react'
import {classNames} from '../wrapper'

// A chip/tag input for an ordered list of short string values (e.g. lab codes).
// Type a value and press Enter (or comma) to add it; click × or press Backspace on an
// empty field to remove. Order is preserved.
const TagInput: React.FC<{
    value: string[];
    onChange: (value: string[]) => void;
    placeholder?: string;
    className?: string;
    // when true, only digits can be entered
    numeric?: boolean;
}> = (props) => {
    const [draft, setDraft] = useState('')

    function add() {
        const value = draft.trim()
        setDraft('')
        if (value.length === 0 || props.value.includes(value)) return
        props.onChange([...props.value, value])
    }

    function removeAt(index: number) {
        props.onChange(props.value.filter((_, i) => i !== index))
    }

    // Reuse the canonical `.input` styling so the chip field matches normal text inputs.
    // `.input` is declared after @tailwind utilities and sets `display: block`, so the
    // layout override to flex must be `!important` (!flex) to win the cascade. `focus-within`
    // mirrors the normal input's `:focus` border, since focus lives on the inner <input>.
    return <div className={classNames(
        'input bg-white !flex flex-wrap gap-1 items-center focus-within:border-primary-500 focus-within:ring-primary-500',
        props.className ?? '')}>
        {props.value.map((tag, index) => (
            <span key={index}
                  className="inline-flex items-center bg-gray-200 text-gray-800 rounded px-2 py-0.5 text-sm">
                {tag}
                <button type="button" className="ml-1 text-gray-500 hover:text-red-600"
                        onClick={() => removeAt(index)}>×</button>
            </span>
        ))}
        <input
            className="flex-1 outline-none bg-transparent text-gray-800 sm:text-sm"
            value={draft}
            placeholder={props.value.length === 0 ? (props.placeholder ?? 'Type a code, press Enter') : ''}
            inputMode={props.numeric ? 'numeric' : undefined}
            onChange={e => setDraft(props.numeric ? e.target.value.replace(/\D/g, '') : e.target.value)}
            onBlur={() => add()}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault()
                    add()
                } else if (e.key === 'Backspace' && draft === '' && props.value.length > 0) {
                    removeAt(props.value.length - 1)
                }
            }}
        />
    </div>
}

export default TagInput
